import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ hero }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = [hero.line1, hero.line2, hero.line3].filter(Boolean);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODQ1Njg3MDJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-jauja-bg/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-jauja-bg via-jauja-bg/50 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="mx-auto max-w-7xl w-full px-5 sm:px-8 pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-jauja-lime">
            <span className="h-px w-8 bg-jauja-lime/60" />
            {hero.overline}
          </span>
        </motion.div>

        <h1 className="mt-6 font-display font-semibold tracking-tighter leading-[0.92] text-white text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {i === lines.length - 1 ? <span className="text-jauja-lime">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
        >
          {hero.subtitle}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
        >
          <button
            onClick={() => document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-cta"
            className="inline-flex items-center gap-2 rounded-full bg-jauja-lime px-7 py-4 text-sm font-bold text-jauja-bg transition-transform duration-200 hover:scale-105"
          >
            {hero.cta}
          </button>
          <button
            onClick={() => document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowDown size={16} className="text-jauja-lime" /> Descubre cómo
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
